import { useEffect, useMemo, useState } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { getStripe } from "@/lib/stripe";

function PaymentForm({ amount, campaignName, onSuccess }) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError(null);
    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });
    if (confirmError) {
      setError(confirmError.message || "Payment failed. Please try again.");
      setSubmitting(false);
      return;
    }
    if (paymentIntent && paymentIntent.status === "succeeded") {
      toast.success("Payment confirmed");
      onSuccess();
    } else if (paymentIntent) {
      // Intents that resolve async (bank debits, etc.) won't be immediately succeeded.
      // Webhook will promote the campaign when it completes.
      toast.info("Payment submitted — campaign will activate once confirmed by your bank.");
      onSuccess();
    }
    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-md bg-muted px-3 py-2 text-sm">
        <div className="font-medium">{campaignName}</div>
        <div className="text-2xl font-semibold">
          ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
        <div className="text-muted-foreground text-xs">One-time prepayment for campaign duration</div>
      </div>
      <PaymentElement options={{ layout: "tabs" }} />
      {error && <div className="text-sm text-destructive">{error}</div>}
      <div className="flex justify-end gap-2 pt-2">
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? "Processing…" : `Pay $${amount.toFixed(2)}`}
        </Button>
      </div>
    </form>
  );
}

export function CampaignPaymentModal({ open, onOpenChange, clientSecret, amount, campaignName, token, onSuccess }) {
  const [stripePromise, setStripePromise] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !token) return;
    setLoadError(null);
    getStripe(token)
      .then(setStripePromise)
      .catch((err) => {
        console.error("Failed to load Stripe:", err);
        setLoadError("Could not load payment form. Please try again.");
      });
  }, [open, token]);

  const options = useMemo(() => (clientSecret ? { clientSecret, appearance: { theme: "stripe" } } : null), [clientSecret]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Complete payment</DialogTitle>
          <DialogDescription>
            Payment is processed securely by Stripe. We never see or store your card details.
          </DialogDescription>
        </DialogHeader>
        {loadError && <div className="text-sm text-destructive">{loadError}</div>}
        {!loadError && stripePromise && options && (
          <Elements stripe={stripePromise} options={options}>
            <PaymentForm amount={amount} campaignName={campaignName} onSuccess={onSuccess} />
          </Elements>
        )}
        {!loadError && !stripePromise && <div className="text-sm text-muted-foreground">Loading payment form…</div>}
      </DialogContent>
    </Dialog>
  );
}

export default CampaignPaymentModal;
